import { useEffect, useState } from 'react';
import type { Project } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { RisksWorkbench } from '@/features/risks/RisksWorkbench';
import { EditableActivityPlan } from '@/features/plans/EditableActivityPlan';
import { PlanificationShopPanel } from '@/features/planification/PlanificationShopPanel';
import { PlanificationSynthesis } from '@/features/planification/PlanificationSynthesis';
import { useToast } from '@/features/ui/toast/useToast';

type Hub = 'menu' | 'risks' | 'prep' | 'deploy' | 'marketplace';

const MODULES = [
  {
    key: 'risks' as const,
    title: 'Maîtrise des risques',
    description: 'Identification, analyse/évaluation et plan de maîtrise assistés par l’IA.',
    tone: 'orange',
    step: '01',
    cta: 'Ouvrir',
  },
  {
    key: 'prep' as const,
    title: 'Plan de préparation',
    description: '6 axes fixes — génerez les actions prioritaires puis complétez le tableau.',
    tone: 'blue',
    step: '02',
    cta: 'Ouvrir',
  },
  {
    key: 'deploy' as const,
    title: 'Plan de déploiement',
    description: '6 axes fixes — même modèle que le plan de préparation.',
    tone: 'green',
    step: '03',
    cta: 'Ouvrir',
  },
  {
    key: 'marketplace' as const,
    title: 'ISBD MarketPlace',
    description: 'Créez votre boutique (1 max) et gérez jusqu’à 10 produits.',
    tone: 'teal',
    step: '04',
    cta: 'Ouvrir',
  },
] as const;

export function PlanificationPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [hub, setHub] = useState<Hub>('menu');
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');

  useEffect(() => {
    if (!profile) return;
    void supabase
      .from('projects')
      .select('*')
      .eq('user_id', profile.id)
      .eq('documents_complete', true)
      .then(({ data, error: err }) => {
        if (err) toast.error(err.message);
        else {
          const list = (data ?? []) as Project[];
          setProjects(list);
          if (list[0]) setProjectId((prev) => prev || list[0]!.id);
          else setProjectId('');
        }
      });
  }, [profile, toast]);

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">
            Réservé aux projets dont le Business Model et le Business Plan sont complets.
          </p>
        </div>
        <div className="u-actions">
          <div className="u-filters">
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              {projects.length === 0 ? <option value="">Aucun projet éligible</option> : null}
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          {hub !== 'menu' ? (
            <button type="button" className="btn btn-ghost" onClick={() => setHub('menu')}>
              ← Retour hub
            </button>
          ) : null}
        </div>
      </div>

      {projectId ? <PlanificationSynthesis projectId={projectId} /> : null}

      {hub === 'menu' ? (
        <div>
          <h3 className="u-section-title">Modules</h3>
          <div className="u-modules">
            {MODULES.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`u-module u-module--${m.tone}`}
                disabled={!projectId}
                onClick={() => setHub(m.key)}
              >
                <div className="u-module-top">
                  <span className="u-module-step">{m.step}</span>
                  <div className={`u-module-icon ${m.tone}`}>{m.title.charAt(0)}</div>
                </div>
                <h3>{m.title}</h3>
                <p>{m.description}</p>
                <span className="u-module-cta">{m.cta}</span>
              </button>
            ))}
          </div>
          {!projectId ? (
            <p className="u-empty">
              Aucun projet éligible. Complétez d’abord le Business Model Canvas et le Business Plan dans
              Conception.
            </p>
          ) : null}
        </div>
      ) : null}

      {hub === 'risks' && projectId ? (
        <div className="u-card">
          <RisksWorkbench projectId={projectId} />
        </div>
      ) : null}
      {hub === 'prep' && projectId ? (
        <div className="u-card">
          <EditableActivityPlan projectId={projectId} kind="prep" />
        </div>
      ) : null}
      {hub === 'deploy' && projectId ? (
        <div className="u-card">
          <EditableActivityPlan projectId={projectId} kind="deploy" />
        </div>
      ) : null}
      {hub === 'marketplace' && projectId ? (
        <div className="u-card">
          <PlanificationShopPanel projectId={projectId} />
        </div>
      ) : null}
    </div>
  );
}
